import { purchase } from 'js-api-client';
import getPaymentEngineAndProvider from 'utils/getPaymentEngineAndProvider';
import { requestPayment, updatePurchase } from './purchase';
import { paymentFailureHandler, paymentPollingHandler, getPaymentPayload } from './payment';
import googlePayFactory from '../../payments/core/factories/googlepay';

/**
 * Initiates a payment attempt using Google Pay.
 *
 * Unlike Apple Pay, Google Pay does not require async coordination callbacks
 * because the payment sheet closes automatically before onSubmit fires.
 * The polling handler and error handler are used directly without wrappers.
 *
 * @param {Object} googlePayData - googlePayPayload, purchaseToken.
 * @param {Object} googlePayData.googlePayPayload - The payload from Google Pay SDK (state.data with paymentMethod and browserInfo).
 * @param {string} googlePayData.purchaseToken - The token associated with the purchase.
 * @returns {Function} A thunk action that dispatches actions based on the Google Pay payment process.
 */
export function googlePayPaymentAttempt(googlePayData) {
  const { googlePayPayload, purchaseToken } = googlePayData;

  return async (dispatch, getState) => {
    dispatch(updatePurchase(true));
    dispatch(requestPayment());

    const { purchase: purchaseState } = getState();
    const selectedPaymentMethod = purchaseState.get('selectedPaymentMethod');
    const { engine: paymentEngine, provider: paymentProvider } = getPaymentEngineAndProvider(
      selectedPaymentMethod,
      { defaultEngine: 'adyen', defaultProvider: 'google_pay' },
    );

    try {
      const googlePayPayment = googlePayFactory.create({ engine: paymentEngine });

      const enginePayload = await googlePayPayment.createPayload(googlePayPayload);

      const paymentPayload = {
        ...getPaymentPayload('google_pay', paymentEngine),
        payment_provider: paymentProvider,
        ...enginePayload,
      };

      await purchase
        .createPayment(purchaseToken, paymentPayload, {
          onReceivePayment: (response) => {
            paymentPollingHandler(dispatch, purchaseToken, response);
          },
        })
        .catch((error) => {
          paymentFailureHandler(dispatch, error);
        });
    } catch (error) {
      paymentFailureHandler(dispatch, error);
    }
  };
}

export default googlePayPaymentAttempt;
